import {
  readonly, ref, useContext,
} from '@nuxtjs/composition-api';
import { Logger } from '~/helpers/logger';
import { BillingCartAddress, Maybe } from '~/modules/GraphQL/types';
import { saveBillingAddressCommand } from '~/modules/checkout/composables/useBilling/commands/saveBillingAddressCommand';
import useShippingProvider from '~/modules/checkout/composables/useShippingProvider';
import useCart from '~/modules/checkout/composables/useCart';
import type { VatIdData, PLVatIdData, CheckoutAddressForm } from '~/helpers/checkout/address';
import { addressFromPLVatIdApiToForm, addressFromVatIdApiToForm } from '~/helpers/checkout/address';
import type {
  UseBillingError,
  UseBillingInterface,
  UseBillingSaveParams,
} from './useBilling';

/**
 * Allows loading and saving billing information
 * of the current cart.
 *
 * See the {@link UseBillingInterface} for a list of methods and values available in this composable.
 */
export function useBilling(): UseBillingInterface {
  const context = useContext();
  const { load: loadShippingAddress, save: saveShippingAddress } = useShippingProvider();
  const { cart } = useCart();

  const loading = ref(false);
  const error = ref<UseBillingError>({
    load: null,
    save: null,
    loadVatIdData: null,
  });

  const load = (): Maybe<BillingCartAddress> => {
    Logger.debug('useBilling.load');
    let billingInfo = null;

    try {
      loading.value = true;

      billingInfo = cart?.value?.billing_address ?? null;
      error.value.load = null;
    } catch (err) {
      error.value.load = err;
      Logger.error('useBilling/load', err);
    } finally {
      loading.value = false;
    }

    return billingInfo;
  };

  const loadVatIdData = async ({
    vatId,
    countryCode,
    isPolishVat,
  }: {
    vatId: string;
    countryCode: string;
    isPolishVat: boolean;
  }): Promise<Maybe<CheckoutAddressForm>> => {
    Logger.debug('useBilling.loadVatIdData');
    let data = null;

    try {
      if (!vatId || (!isPolishVat && !countryCode)) {
        error.value.loadVatIdData = {
          message: context.i18n.t(
            isPolishVat
              ? 'Please provide a valid VAT ID'
              : 'Please provide a valid VAT ID and country code',
          ) as string,
          name: 'by hand',
        };
        return null;
      }

      loading.value = true;

      const endpoint = isPolishVat
        ? `${context.env.URL_NIP_DETAILS}?nip=${vatId}`
        : 'https://ec.europa.eu/taxation_customs/vies/rest-api//check-vat-number';

      const options = isPolishVat
        ? undefined
        : {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ countryCode, vatNumber: vatId }),
        };

      const response = await fetch(endpoint, options);
      const fetchData = await response.json();

      if (isPolishVat) {
        if (fetchData.error) {
          error.value.loadVatIdData = {
            message: context.i18n.t(
              'There is no company in the GUS database with the given tax identification number',
            ) as string,
            name: 'not found',
          };
          return null;
        }
        data = addressFromPLVatIdApiToForm(fetchData.response as PLVatIdData);
      } else {
        if (!fetchData.valid) {
          error.value.loadVatIdData = {
            message: context.i18n.t(
              'There is no company in the VIES database with the given tax identification number and country',
            ) as string,
            name: 'not found',
          };
          return null;
        }
        data = addressFromVatIdApiToForm(fetchData.response as VatIdData);
      }

      error.value.loadVatIdData = null;
    } catch (err) {
      const isClientError = err.status >= 400 && err.status < 500;

      error.value.loadVatIdData = {
        message: context.i18n.t(
          isPolishVat
            ? (isClientError
              ? 'There is no company in the GUS database with the given tax identification number'
              : 'GUS database is temporarily unavailable, please enter data manually')
            : 'VIES database is temporarily unavailable, please enter data manually',
        ) as string,
        name: isClientError ? 'not found' : 'by hand',
      };

      Logger.error('useBilling/loadVatIdData', err);
    } finally {
      loading.value = false;
    }

    return data;
  };

  const save = async ({ billingDetails, customQuery = null, customHeaders = {} }: UseBillingSaveParams): Promise<Maybe<BillingCartAddress>> => {
    Logger.debug('useBilling.save');
    let billingInfo = null;

    try {
      loading.value = true;
      billingInfo = await saveBillingAddressCommand.execute(context, cart.value.id, billingDetails, customQuery, customHeaders);

      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      /**
       * This is a workaround needed due to Magento GraphQL API
       * cleaning the Shipping method after defining the billing address
       */
      const shippingMethod = await loadShippingAddress();
      Logger.debug('[Magento]: Defining the shipping method as:', JSON.stringify(shippingMethod, null, 2));

      if (shippingMethod) {
        await saveShippingAddress({
          shippingMethod: {
            carrier_code: shippingMethod.carrier_code,
            method_code: shippingMethod.method_code,
          },
        });
      }
      /**
       * End of GraphQL Workaround
       */
      error.value.save = null;
    } catch (err) {
      error.value.save = err;
      Logger.error('useBilling/save', err);
    } finally {
      loading.value = false;
    }

    return billingInfo;
  };

  return {
    load,
    save,
    loadVatIdData,
    error: readonly(error),
    loading: readonly(loading),
  };
}

export * from './useBilling';
export default useBilling;
